import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { authService } from '@/services/auth';
import { createApiClient, updateApiKey, clearApiKey } from '@/services/zodios-client';
import type { AuthState, TenantInfo } from '@/types';

export const useAuthStore = defineStore('auth', () => {
  // State
  const apiKey = ref<string | null>(null);
  const tenantInfo = ref<TenantInfo | null>(null);
  const isAuthenticated = ref(false);
  const isLoading = ref(false);
  const isAdmin = ref(false);

  // Computed
  const authState = computed<AuthState>(() => ({
    apiKey: apiKey.value,
    tenantInfo: tenantInfo.value,
    isAuthenticated: isAuthenticated.value,
    isLoading: isLoading.value,
    isAdmin: isAdmin.value,
  }));

  // Actions
  const initializeAuth = async () => {
    const storedApiKey = authService.getStoredApiKey();
    if (storedApiKey) {
      try {
        // Initialize the client immediately to avoid a redirect flicker
        const baseURL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';
        createApiClient(baseURL, storedApiKey);
        updateApiKey(storedApiKey);

        // Optimistically mark as authenticated and admin; we'll confirm shortly
        apiKey.value = storedApiKey;
        isAuthenticated.value = true;

        // Confirm admin access in background
        const result = await authService.testApiKey(storedApiKey);
        isAdmin.value = !!result.isAdmin;
        if (!result.isValid) {
          logout();
        }
      } catch {
        logout();
      }
    }
  };

  const login = async (newApiKey: string) => {
    try {
      isLoading.value = true;

      // Validate API key format
      if (!authService.validateApiKeyFormat(newApiKey)) {
        throw new Error('Invalid API key format');
      }

      // Initialize API client
      const baseURL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';
      createApiClient(baseURL, newApiKey);
      
      // Update the API key in the client
      updateApiKey(newApiKey);

      // Test the API key
      const result = await authService.testApiKey(newApiKey);
      
      if (!result.isValid) {
        throw new Error(result.error || 'Invalid API key');
      }

      // Store the API key
      apiKey.value = newApiKey;
      isAuthenticated.value = true;
      isAdmin.value = result.isAdmin;
      
      // Store in localStorage
      authService.storeApiKey(newApiKey);

      // Admin status is already confirmed from testApiKey
      // No need for additional user info call

    } catch (error: any) {
      logout();
      throw error;
    } finally {
      isLoading.value = false;
    }
  };

  const logout = () => {
    apiKey.value = null;
    tenantInfo.value = null;
    isAuthenticated.value = false;
    isLoading.value = false;
    isAdmin.value = false;
    
    // Clear from localStorage
    authService.clearStoredApiKey();
    
    // Clear API client headers
    clearApiKey();
  };

  const updateStoredApiKey = (newApiKey: string) => {
    if (apiKey.value) {
      updateApiKey(newApiKey);
      authService.storeApiKey(newApiKey);
    }
  };

  return {
    // State
    apiKey,
    tenantInfo,
    isAuthenticated,
    isLoading,
    isAdmin,
    
    // Computed
    authState,
    
    // Actions
    initializeAuth,
    login,
    logout,
    updateStoredApiKey,
  };
});
